'use client';

import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface AccessDeniedProps {
  /** The name of the page/feature being accessed (e.g., "Dashboard", "Reports", "Settings") */
  pageName: string;
}

/**
 * AccessDenied component
 *
 * Shows a consistent "Access Denied" message when users try to access pages
 * they don&apos;t have permissions for.
 *
 * Design decision: No action button is shown because we cannot predict which
 * pages the user has access to. The sidebar navigation will automatically
 * filter to show only accessible pages.
 */
export default function AccessDenied({ pageName }: AccessDeniedProps) {
  const { darkMode } = useDarkMode();

  return (
    <div className="flex h-[calc(100vh-16rem)] items-center justify-center">
      <div className="max-w-md text-center">
        {/* Lock icon */}
        <svg
          className={`mx-auto h-12 w-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>

        {/* Heading */}
        <h3
          className={`mt-4 text-base font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
        >
          Access Denied
        </h3>

        {/* Message - two lines */}
        <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          You don&apos;t have permission
          <br />
          to access {pageName}.
        </p>
      </div>
    </div>
  );
}
